import type { ColumnDef, Row } from '@tanstack/react-table';
import type React from 'react';
import { memo } from 'react';
import { DataTable } from '@/components/ui/data-table';
import type { FilterField, QuickFilterPreset } from '@/components/ui/data-table-filter-toolbar';
import type { DocumentDeleteOptions } from '@/types/document';

interface IsolatedDataTableProps<TData extends { id: string; is_draft?: boolean; document_type?: any }, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  refreshData: () => void;
  onRowsSelected: (rows: string[]) => void;
  onDelete: (selectedIds: string[], options?: DocumentDeleteOptions) => void | Promise<void>;
  getDeleteAttachmentCount?: (rows: TData[]) => number;
  isDeleting: boolean;
  isLoading: boolean;
  syncButton: React.ReactNode;
  slug: string;
  documentType: string;
  documentTypeSlug: string;
  folderGuid?: string;
  enableFilenameSuggestions?: boolean;
  onDropFiles?: (files: File[]) => Promise<void>;
  onFilesDroppedOnFolder?: (files: File[], folderId: number) => void;
  isProcessingDrop?: boolean;
  onEmptyStateClick?: () => void;
  showQuickFilters?: boolean;
  /** Field config for the Filters menu shown above the table (replaces per-column header filters) */
  filterFields?: FilterField[];
  /** Small page-level control rendered beside the Filters button. */
  toolbarExtra?: React.ReactNode;
  /** One-tap quick-filter presets shown alongside the Filters menu */
  quickFilterPresets?: QuickFilterPreset[];
  initialPageSize?: number;
  enableBulkTags?: boolean;
  renderSubComponent?: (row: Row<TData>) => React.ReactElement;
  /** Renders sub-rows as actual <tr> elements for column alignment with parent rows */
  renderSubRows?: (row: Row<TData>) => React.ReactNode;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  getSubRows?: (row: TData) => TData[] | undefined;
  /** Optional row click handler invoked for all selection modes (receives the click event) */
  onAnyRowClick?: (row: TData, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Optional dynamic className for each row */
  getRowClassName?: (row: TData) => string | undefined;
  /** Single-click on non-interactive row areas (e.g. open document in side panel) */
  onRowActivate?: (row: TData) => void;
  onRowDoubleClick?: (row: TData) => void;
  deepAnalysisEnabled?: boolean;
}

// Memoized DataTable wrapper that prevents re-renders from parent state changes
export const IsolatedDataTable = memo(function IsolatedDataTable<
  TData extends { id: string; is_draft?: boolean; document_type?: any },
  TValue
>({
  columns,
  data,
  refreshData,
  onRowsSelected,
  onDelete,
  getDeleteAttachmentCount,
  isDeleting,
  isLoading,
  syncButton,
  slug,
  documentType,
  documentTypeSlug,
  folderGuid,
  enableFilenameSuggestions = true,
  onDropFiles,
  onFilesDroppedOnFolder,
  isProcessingDrop,
  onEmptyStateClick,
  showQuickFilters,
  filterFields,
  toolbarExtra,
  quickFilterPresets,
  initialPageSize,
  enableBulkTags,
  renderSubComponent,
  renderSubRows,
  getRowCanExpand,
  getSubRows,
  onAnyRowClick,
  getRowClassName,
  onRowActivate,
  onRowDoubleClick,
  deepAnalysisEnabled,
}: IsolatedDataTableProps<TData, TValue>) {
  return (
    <DataTable
      columns={columns}
      data={data}
      hasPopupMenu={true}
      uploadButton={false}
      exportButton={false}
      documentType={documentType}
      documentTypeSlug={documentTypeSlug}
      folderGuid={folderGuid}
      matterGuid={slug}
      searchPlaceholder={"Find documents..."}
      enableFilenameSuggestions={enableFilenameSuggestions}
      onUploadComplete={refreshData}
      onRowsSelected={onRowsSelected}
      onDelete={onDelete}
      getDeleteAttachmentCount={getDeleteAttachmentCount}
      isDeleting={isDeleting}
      isLoading={isLoading}
      syncButton={syncButton}
      onDropFiles={onDropFiles}
      onFilesDroppedOnFolder={onFilesDroppedOnFolder}
      isProcessingDrop={isProcessingDrop}
      onEmptyStateClick={onEmptyStateClick}
      showQuickFilters={showQuickFilters}
      filterFields={filterFields}
      toolbarExtra={toolbarExtra}
      quickFilterPresets={quickFilterPresets}
      initialPageSize={initialPageSize}
      enableBulkTags={enableBulkTags}
      renderSubComponent={renderSubComponent}
      renderSubRows={renderSubRows}
      getRowCanExpand={getRowCanExpand}
      getSubRows={getSubRows}
      onAnyRowClick={onAnyRowClick}
      getRowClassName={getRowClassName}
      onRowActivate={onRowActivate}
      onRowDoubleClick={onRowDoubleClick}
      deepAnalysisEnabled={deepAnalysisEnabled}
    />
  );
}) as <TData extends { id: string; is_draft?: boolean; document_type?: any }, TValue>(
  props: IsolatedDataTableProps<TData, TValue>
) => React.JSX.Element;
